const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5000/api";

const authHeaders = (token: string) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

export interface DashboardStats {
  upcomingDaysLeft: number | null;
  activeRegistrations: number;
  totalIdeas: number;
  mentorSessions: number;
}

export interface ActiveHackathon {
  _id: string;
  title: string;
  organizerName: string;
  currentPhase: string;
  daysLeft: number;
  nextPhaseName: string;
  nextPhaseDate: string;
  team: {
    name: string;
    members: { initials: string; color: string }[];
    memberCount: number;
  } | null;
  progressSteps: { label: string; status: "completed" | "current" | "upcoming" }[];
}

export interface ReadinessData {
  percentage: number;
  ideasSubmitted: number;
  teamFormed: number;
  mentorSessions: number;
  milestonesHit: number;
  milestonesTotal: number;
}

export interface IdeaStatusBreakdown {
  name: string;
  value: number;
}

export interface WeeklyTrend {
  week: string;
  ideas: number;
  teams: number;
}

export interface DeadlineInfo {
  date: string;
  daysLeft: number;
  label: string;
}

export interface FeedbackInfo {
  pendingCount: number;
  latestReviewerName: string | null;
}

/** Fetch summary stats for the dashboard stat cards */
export async function fetchDashboardStats(token: string): Promise<DashboardStats> {
  const [regRes, ideaRes] = await Promise.all([
    fetch(`${BASE}/hackathon-register/my-registrations`, { headers: authHeaders(token) }),
    fetch(`${BASE}/ideas`, { headers: authHeaders(token) }),
  ]);

  const registrations = regRes.ok ? await regRes.json() : { data: [] };
  const ideasData = ideaRes.ok ? await ideaRes.json() : { items: [], pagination: { total: 0 } };

  const regs: any[] = registrations.data ?? [];
  const totalIdeas: number = ideasData.pagination?.total ?? ideasData.items?.length ?? 0;

  // Find the nearest hackathon whose startDate is still in the future (truly upcoming)
  let upcomingDaysLeft: number | null = null;
  const now = new Date();
  for (const reg of regs) {
    const start = reg.hackathon?.startDate ? new Date(reg.hackathon.startDate) : null;
    if (start && start > now) {
      const days = Math.ceil((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (upcomingDaysLeft === null || days < upcomingDaysLeft) upcomingDaysLeft = days;
    }
  }

  return {
    upcomingDaysLeft,
    activeRegistrations: regs.length,
    totalIdeas,
    mentorSessions: 0, // extend when mentor-session endpoint exists
  };
}

/** Fetch the user's most recent active hackathon registration with team info */
export async function fetchActiveHackathon(token: string): Promise<ActiveHackathon | null> {
  const res = await fetch(`${BASE}/hackathon-register/my-registrations`, {
    headers: authHeaders(token),
  });
  if (!res.ok) return null;

  const json = await res.json();
  const regs: any[] = json.data ?? [];
  if (!regs.length) return null;

  const now = new Date();

  // Pick the registration whose hackathon end date is still in the future,
  // or fall back to the most recent one
  const active =
    regs.find((r: any) => {
      const end = r.hackathon?.endDate ? new Date(r.hackathon.endDate) : null;
      return end && end > now;
    }) ?? regs[0];

  const hackathon = active.hackathon ?? {};
  const team      = active.team      ?? null;

  // Days left until hackathon end
  const endDate  = hackathon.endDate ? new Date(hackathon.endDate) : null;
  const daysLeft = endDate
    ? Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  // Member avatars — team.members[].name from Team model
  const AVATAR_COLORS = ["bg-teal-600", "bg-indigo-600", "bg-red-500", "bg-purple-600", "bg-orange-500"];
  const members = ((team?.members ?? []) as any[])
    .slice(0, 3)
    .map((m: any, i: number) => ({
      initials: (m.name ?? "?")
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2),
      color: AVATAR_COLORS[i % AVATAR_COLORS.length],
    }));

  // Phase windows from HackathonEvent.phases
  const phases  = hackathon.phases ?? {};
  const regEnd  = phases.registration?.endDate  ? new Date(phases.registration.endDate)  : null;
  const subEnd  = phases.submission?.endDate    ? new Date(phases.submission.endDate)    : null;
  const evalEnd = phases.evaluation?.endDate    ? new Date(phases.evaluation.endDate)    : null;

  const regDone  = regEnd  ? now > regEnd  : true;
  const subDone  = subEnd  ? now > subEnd  : false;
  const evalDone = evalEnd ? now > evalEnd : false;

  const subCurrent  = regDone  && !subDone;
  const evalCurrent = subDone  && !evalDone;

  const progressSteps: ActiveHackathon["progressSteps"] = [
    { label: "Registration", status: regDone  ? "completed" : "current"   },
    { label: "Submission",   status: subDone  ? "completed" : subCurrent  ? "current" : "upcoming" },
    { label: "Evaluation",   status: evalDone ? "completed" : evalCurrent ? "current" : "upcoming" },
  ];

  const currentPhase = evalCurrent ? "EVALUATION PHASE"
    : subCurrent  ? "SUBMISSION PHASE"
    : regDone     ? "COMPLETED"
    : "REGISTRATION PHASE";

  // Next phase label + formatted date
  const nextPhaseName = subCurrent ? "Evaluation" : evalCurrent ? "Completed" : "Submission";
  const nextPhaseRaw  = subCurrent ? evalEnd : subEnd;
  const nextPhaseDate = nextPhaseRaw
    ? nextPhaseRaw.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";

  return {
    _id:          hackathon._id   ?? active._id,
    title:        hackathon.title ?? "Hackathon",
    organizerName: hackathon.organizerName ?? "",
    currentPhase,
    daysLeft,
    nextPhaseName,
    nextPhaseDate,
    team: team
      ? {
          // Team model uses teamName, not name
          name:        team.teamName ?? team.name ?? "Your Team",
          members,
          memberCount: (team.members ?? []).length,
        }
      : null,
    progressSteps,
  };
}

/** Fetch idea status breakdown for the donut chart */
export async function fetchIdeaStatusBreakdown(token: string): Promise<IdeaStatusBreakdown[]> {
  const res = await fetch(`${BASE}/ideas`, { headers: authHeaders(token) });
  if (!res.ok) return [];

  const json = await res.json();
  const items: any[] = json.items ?? [];

  const counts: Record<string, number> = {};
  for (const idea of items) {
    const s: string = idea.status ?? "unknown";
    counts[s] = (counts[s] ?? 0) + 1;
  }

  const labelMap: Record<string, string> = {
    submitted_initial: "Submitted",
    under_mentor_review: "In Review",
    submitted_final: "Final",
    selected: "Selected",
    rejected: "Rejected",
  };

  return Object.entries(counts).map(([key, value]) => ({
    name: labelMap[key] ?? key,
    value,
  }));
}

/** Derive weekly trends from ideas list (grouped by submission week) */
export async function fetchWeeklyTrends(token: string): Promise<WeeklyTrend[]> {
  const res = await fetch(`${BASE}/ideas`, { headers: authHeaders(token) });
  if (!res.ok) return [];

  const json = await res.json();
  const items: any[] = json.items ?? [];

  const weekMap: Record<string, { ideas: number; teams: Set<string> }> = {};

  for (const idea of items) {
    const date = new Date(idea.submittedAt ?? idea.createdAt);
    const weekNum = getISOWeek(date);
    const key = `W${weekNum}`;
    if (!weekMap[key]) weekMap[key] = { ideas: 0, teams: new Set() };
    weekMap[key].ideas += 1;
    if (idea.teamId) weekMap[key].teams.add(String(idea.teamId));
  }

  return Object.entries(weekMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-7)
    .map(([week, v]) => ({ week, ideas: v.ideas, teams: v.teams.size }));
}

function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/** Fetch next deadline from active hackathon */
export async function fetchNextDeadline(token: string): Promise<DeadlineInfo | null> {
  const active = await fetchActiveHackathon(token);
  if (!active) return null;
  return {
    date: active.nextPhaseDate,
    daysLeft: active.daysLeft,
    label: `${active.nextPhaseName} closes in ${active.daysLeft} day${active.daysLeft !== 1 ? "s" : ""}`,
  };
}

/** Fetch pending mentor feedback count */
export async function fetchFeedbackInfo(token: string): Promise<FeedbackInfo> {
  const res = await fetch(`${BASE}/ideas`, { headers: authHeaders(token) });
  if (!res.ok) return { pendingCount: 0, latestReviewerName: null };

  const json = await res.json();
  const items: any[] = json.items ?? [];
  const underReview = items.filter((i: any) => i.status === "under_mentor_review");

  return {
    pendingCount: underReview.length,
    latestReviewerName: null, // extend when feedback endpoint returns reviewer name
  };
}

export interface TrackingMilestone {
  title: string;
  date: string;
  description: string;
  status: "Completed" | "Active" | "Upcoming";
}

export interface TrackingData {
  hackathonTitle: string;
  completedCount: number;
  totalCount: number;
  milestones: TrackingMilestone[];
}

/** Fetch dynamic milestone tracking data for a specific hackathon */
export async function fetchTrackingData(token: string, hackathonId: string): Promise<TrackingData | null> {
  const res = await fetch(`${BASE}/hackathon-events/${hackathonId}`, { headers: authHeaders(token) });
  if (!res.ok) return null;

  const json = await res.json();
  const h = json.data ?? json;
  const phases = h.phases ?? {};
  const now = new Date();

  const fmt = (d: string | Date | undefined) =>
    d ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "TBD";

  const toStatus = (start: Date | null, end: Date | null): "Completed" | "Active" | "Upcoming" => {
    if (!start || !end) return "Upcoming";
    if (now > end) return "Completed";
    if (now >= start) return "Active";
    return "Upcoming";
  };

  const reg = phases.registration ?? {};
  const sub = phases.submission ?? {};
  const eva = phases.evaluation ?? {};

  const milestones: TrackingMilestone[] = [
    {
      title: "Registration",
      date: `${fmt(reg.startDate)} – ${fmt(reg.endDate)}`,
      description: "Registration and team formation",
      status: toStatus(new Date(reg.startDate), new Date(reg.endDate)),
    },
    {
      title: "Initial Idea Submission",
      date: fmt(sub.initialSubmissionEnd),
      description: "Submit your initial idea proposal",
      status: toStatus(new Date(sub.initialSubmissionStart), new Date(sub.initialSubmissionEnd)),
    },
    {
      title: "Mentor Review Window",
      date: `${fmt(sub.initialSubmissionEnd)} – ${fmt(sub.finalSubmissionStart)}`,
      description: "Mentors review team proposals",
      status: toStatus(new Date(sub.initialSubmissionEnd), new Date(sub.finalSubmissionStart)),
    },
    {
      title: "Final Submission",
      date: fmt(sub.finalSubmissionEnd),
      description: "Project final submission deadline",
      status: toStatus(new Date(sub.finalSubmissionStart), new Date(sub.finalSubmissionEnd)),
    },
    {
      title: "Evaluation & Jury Decisions",
      date: fmt(eva.endDate),
      description: "Jury evaluates all final submissions",
      status: toStatus(new Date(eva.startDate), new Date(eva.endDate)),
    },
  ];

  const completedCount = milestones.filter((m) => m.status === "Completed").length;

  return {
    hackathonTitle: h.title ?? "Hackathon",
    completedCount,
    totalCount: milestones.length,
    milestones,
  };
}

/** Fetch readiness data */
export async function fetchReadiness(token: string): Promise<ReadinessData> {
  const [ideasRes, regRes] = await Promise.all([
    fetch(`${BASE}/ideas`, { headers: authHeaders(token) }),
    fetch(`${BASE}/hackathon-register/my-registrations`, { headers: authHeaders(token) }),
  ]);

  const ideasJson = ideasRes.ok ? await ideasRes.json() : { items: [] };
  const regJson = regRes.ok ? await regRes.json() : { data: [] };

  const ideas: any[] = ideasJson.items ?? [];
  const regs: any[] = regJson.data ?? regJson ?? [];

  const ideasSubmitted = ideas.length;
  const teamFormed = regs.length > 0 ? 1 : 0;
  const selected = ideas.filter((i: any) => i.status === "selected").length;
  const milestonesHit = Math.min(ideasSubmitted + teamFormed + selected, 6);
  const milestonesTotal = 6;

  const score = (ideasSubmitted > 0 ? 30 : 0) + (teamFormed ? 25 : 0) + (selected > 0 ? 25 : 0) + (milestonesHit / milestonesTotal) * 20;
  const percentage = Math.round(Math.min(score, 100));

  return {
    percentage,
    ideasSubmitted,
    teamFormed,
    mentorSessions: 0,
    milestonesHit,
    milestonesTotal,
  };
}
